import type { QueryOptions } from '@tanstack/react-query';

import { http } from '@/api/instance';
import { getQueryParameters } from '@/api/query';

export const classAPI = {
  list: ({ ...params }: Record<string, string | number>) =>
    http.get<ListResultsPageSize<Class>>('/api/info/class', { params }).then(({ data }) => data),
  create: (data: ClassForm) => http.post<unknown>(`/api/add/class`, { data }),
  update: (id: number | string, data: ClassForm) => http.put<unknown>(`/api/edit/class/${id}`, { data }),
  delete: (id: number | string) => http.delete<unknown>(`/api/delete/class/${id}`),
  detail: (id: number | string | null) =>
    http.get<{ data: Class }>(`/api/info/class/${id}`).then(({ data }) => data.data),

  section_list: ({ ...params }: Record<string, string | number>) =>
    http.get<ListResultsPageSize<Section>>('/api/info/section', { params }).then(({ data }) => data),
  section_update: (id: number | string, data: SectionForm) => http.put<unknown>(`/api/edit/section/${id}`, { data }),
  section_create: (data: SectionForm) => http.post<unknown>(`/api/add/section`, { data }),

  get_attendance: (id: number | string) =>
    http.get<ObjectData<Attendance>>(`/api/info/attendance/class/${id}`).then(({ data }) => data),
  update_attendance: ({ id, data }: AttendancePayload) =>
    http.put(`/api/edit/attendance/${id}`, { data }).then(({ data }) => data),

  student_score_list: (id: number | string) =>
    http.get<ListDataGetOne<StudentScore>>(`/api/info/student_score/${id}`).then(({ data }) => data.data),
  student_score_update: (payload: StudentScoreUpdatePayload) =>
    http.post('/api/update/student_score', { ...payload }).then((data) => data),
  student_pass_status_update: (payload: StudentPassStatusUpdatePayload) =>
    http.post('/api/update/student_pass_status', { ...payload }).then((data) => data),

  class_to_transfer: ({ ...params }: Record<string, string | number | boolean>) =>
    http
      .get<ListClassToTransfer<ClassToTransfer>>('/api/info/class/classes-to-transfers', { params })
      .then(({ data }) => data),
};

export const classQuery = {
  list: (params: QueryParams<Partial<SearchFormData>>) =>
    ({
      queryKey: ['GetClassList', params] as const,
      queryFn: () => classAPI.list(getQueryParameters(params)),
    }) as const satisfies QueryOptions,
  section_list: (params: QueryParams<Partial<SearchFormData>>) =>
    ({
      queryKey: ['GetSectionList', params] as const,
      queryFn: () => classAPI.section_list(getQueryParameters(params)),
    }) as const satisfies QueryOptions,
  detail: (id: string | number) =>
    ({
      queryKey: ['GetDetailClass', id] as const,
      queryFn: () => classAPI.detail(id),
    }) as const satisfies QueryOptions,
  get_attendance: (id: number | string) => ({
    queryKey: ['GetAttendanceClass', id] as const,
    queryFn: () => classAPI.get_attendance(id),
  }),
  get_student_score_list: (id: number | string) => ({
    queryKey: ['GetStudentScore', id] as const,
    queryFn: () => classAPI.student_score_list(id),
  }),
  get_class_to_transfer: (params: { current_class_id: string | number; exclude_current_class: boolean }) => ({
    queryKey: ['GetClassToTransfer', params] as const,
    queryFn: () => classAPI.class_to_transfer(params),
  }),
};
